import { FunctionComponent, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Section,
  SectionHeading,
  Confirmation,
  BatchApiStatusHandler,
  PlatformFeeInformation,
} from '../components';
import {
  EntityType,
  FormSpec,
  LegalEntity,
  Person,
  Requirement,
} from '../types';
import { cleanIntakeDates } from '../utils/intake';
import { successCallbackPath } from '../utils/paths';
import { getRegionNameByCode } from '../utils/format';
import { fetchApi } from '../utils/fetchApi';
import { ApiStatus, IApiData } from '../utils/types';
import { useGlobalContext } from '../contexts/globalContext';
import { Renderer, formSpecToRendererConfig } from '../components/forms';
import ScForeignQualificationSpec from '../components/forms/specs/sc_foreign_qualification_spec.json';
import TxForeignQualificationSpec from '../components/forms/specs/tx_foreign_qualification_spec.json';
import DefaultCorpForeignQualificationSpec from '../components/forms/specs/default_corp_foreign_qualification_spec.json';
import DefaultLLCForeignQualificationSpec from '../components/forms/specs/default_llc_foreign_qualification_spec.json';
import { useBatchApi } from '../hooks';
import { FormError } from '../components/forms/FormError';
import { useRequirementFee } from '../hooks/useRequirementFee';
import { RequirementFeeText } from '../components/RequirementFeeText';

export const ForeignQualificationReqsByState: Record<string, string[]> = {
  AL: ['0a57727e'],
  AK: ['d44f5d2a'],
  AZ: ['47250fe2'],
  AR: ['f433e742'],
  CA: ['59ad4896', '8f786a65'], // both llc and corp FQ reqs
  CO: ['141d7a9e'],
  CT: ['a31aba07'],
  DC: ['457a47f0'],
  DE: [],
  FL: ['39efb3a0'],
  GA: ['aef7d286'],
  HI: ['590a93f5'],
  ID: ['f29e30c7'],
  IL: ['688399d1'],
  IN: ['e61d4e71'],
  IA: ['6f162b81'],
  KS: ['818248d0'],
  KY: ['2a671273'],
  LA: ['54850433'],
  MA: ['f6f7f3bf'],
  MD: ['05f9b86c'],
  ME: ['f3bfb77c'],
  MI: ['bc75ff3b'],
  MN: ['3a83057b'],
  MO: ['bcbeb2a0'],
  MS: ['661b482b'],
  MT: ['777da247'],
  NC: ['d4874bd8'],
  ND: ['4a271b9b'],
  NE: ['6da09be4'],
  NH: ['1c881d4d'],
  NJ: ['66938053'],
  NM: ['3aa94bc6'],
  NV: ['25161fc2'],
  NY: ['26d6ac72', 'ea60e3bb'], // both llc and corp FQ reqs
  OH: ['b48e87e3'],
  OK: ['34fe5f5e'],
  OR: ['89b3d854'],
  PA: ['b14e7782'],
  RI: ['b7c8a463'],
  SC: ['b8e8da3b'],
  SD: ['86751f6b'],
  TN: ['5619acd7'],
  TX: ['1e3048bc'],
  UT: ['30c93059'],
  VA: ['096d018f'],
  VT: ['7c8653d6'],
  WA: ['acb96efa'],
  WI: ['86001bc0'],
  WV: ['57cefa24'],
  WY: ['1de48302'],
};

export const StateSpecMapping: Record<string, FormSpec> = {
  sc: ScForeignQualificationSpec,
  tx: TxForeignQualificationSpec,
};

type ForeignQualificationProps = {
  locationId: string;
  requirement: Requirement;
  legalEntity: LegalEntity;
  peopleData: Person[];
  callbackUrl?: string | null;
};

export const ForeignQualification: FunctionComponent<
  ForeignQualificationProps
> = ({ locationId, requirement, legalEntity, peopleData, callbackUrl }) => {
  const fee = useRequirementFee(requirement);
  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);
  const [isSubmitSuccessful, setSubmitSuccessful] = useState<boolean>(false);
  const isSpecSupported =
    Object.keys(StateSpecMapping).indexOf(locationId) > -1;

  const navigate = useNavigate();

  const formMethods = useForm();
  const {
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const onSubmit = (data: any) => {
    setHasSubmitError(false);
    if (errors.isNotEmpty) {
      return;
    }

    const cleanedData = cleanIntakeDates(data);
    const payload = isSpecSupported
      ? { cleanedData }
      : { default: cleanedData };

    // Workflow instance automation
    fetchApi({
      url: `/api/legal_entity/locations/${locationId}/foreign_qualification`,
      method: 'POST',
      body: {
        legal_entity_requirement_id: requirement.id,
        data: payload,
      },
    }).then(({ status: status2 }: IApiData) => {
      if (status2 === ApiStatus.Error) {
        setHasSubmitError(true);
      } else {
        if (callbackUrl) {
          navigate(successCallbackPath(callbackUrl), {
            replace: true,
          });
          return;
        }

        setSubmitSuccessful(true);
      }
    });
  };

  // Now that we support LLC and C Corp we need to load specialized
  // versions based on the entity type
  let loadedSpec: FormSpec;
  if (legalEntity.entity_type === EntityType.CCorp) {
    loadedSpec = isSpecSupported
      ? StateSpecMapping[locationId]
      : DefaultCorpForeignQualificationSpec;
  } else if (legalEntity.entity_type === EntityType.LLC) {
    loadedSpec = DefaultLLCForeignQualificationSpec;
  } else {
    // Final fallback
    loadedSpec = DefaultCorpForeignQualificationSpec;
  }
  return (
    <Section className="flex flex-col items-center">
      {isSubmitSuccessful ? (
        <Confirmation
          heading="Success"
          description="Your request is on its way"
        />
      ) : (
        <>
          <SectionHeading
            className="w-96 pb-4"
            text={
              requirement.title ||
              getRegionNameByCode(locationId) + ' Foreign Qualification'
            }
          />
          <FormProvider {...formMethods}>
            <form className="my-8 w-96" onSubmit={handleSubmit(onSubmit)}>
              <Renderer
                config={formSpecToRendererConfig(loadedSpec)}
                errors={errors}
                peopleData={peopleData}
              />
              <p className="mt-4 flex flex-col">
                <Button type="submit" size="large">
                  Submit
                </Button>
                <PlatformFeeInformation
                  requirement={requirement}
                  regionCode={requirement.region[0]}
                  domesticFee={requirement.domestic_fee}
                  foreignFee={requirement.foreign_fee}
                  baseFee={200}
                  displayRegisteredAgentFee={true}
                />
                {hasSubmitError && <FormError />}
                {fee && (
                  <div className="mt-4">
                    <RequirementFeeText fee={fee} />
                  </div>
                )}
              </p>
            </form>
          </FormProvider>
        </>
      )}
    </Section>
  );
};

export const ForeignQualificationView: FunctionComponent = () => {
  const { locationId, requirementId } = useParams<Record<string, string>>();
  if (!locationId || !requirementId) {
    throw Error('Location or requirement missing');
  }

  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get('callback_url');

  const batchResponse = useBatchApi([
    {
      url: `/api/requirements/${requirementId}`,
      method: 'POST',
    },
    {
      url: '/api/legal_entity/people',
      method: 'GET',
    },
  ]);

  const { globalState } = useGlobalContext();
  const { legal_entity: legalEntity } = globalState.user;

  const componentPropsFn = ([
    reqResponse,
    peopleData,
  ]: any[]): ForeignQualificationProps => {
    return {
      locationId,
      requirement: reqResponse,
      peopleData,
      legalEntity,
      callbackUrl,
    };
  };

  return (
    <BatchApiStatusHandler
      batchResponse={batchResponse}
      component={ForeignQualification}
      componentProps={componentPropsFn}
    />
  );
};
